<template>
  <div class="approve-success-page gm-page pa-4" :class="{ 'approve-success-page--reduced': reducedMotion }">
    <div class="approve-success gm-card rounded-xl">
      <FireworksCelebration v-if="showFireworks" @complete="onFireworksComplete" />

      <div class="approve-success__glow approve-success__glow--a" aria-hidden="true" />
      <div class="approve-success__glow approve-success__glow--b" aria-hidden="true" />

      <div class="approve-success__content">
        <ApproveSuccessIcon :reduced-motion="reducedMotion" />

        <h1 class="approve-success__title">投运成功</h1>
        <p class="approve-success__station">{{ stationName }}</p>
        <p class="approve-success__desc">电站已完成审核并正式投运，开始为电网提供储能服务。</p>

        <div class="approve-success__stats">
          <div v-for="item in statItems" :key="item.label" class="approve-success__stat gm-border">
            <span class="approve-success__stat-value">{{ item.value }}</span>
            <span class="approve-success__stat-label">{{ item.label }}</span>
          </div>
        </div>
      </div>

      <footer class="approve-success__footer">
        <v-btn color="primary" variant="flat" size="large" rounded="lg" class="text-none approve-success__back-btn"
          prepend-icon="mdi-format-list-bulleted" @click="goIndex">
          返回审批列表
        </v-btn>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import FireworksCelebration from './components/FireworksCelebration.vue'
import ApproveSuccessIcon from './components/ApproveSuccessIcon.vue'

const route = useRoute()
const router = useRouter()
const reducedMotion = ref(false)
const fireworksDone = ref(false)

const showFireworks = computed(() => !reducedMotion.value && !fireworksDone.value)

const stationName = computed(
  () => String(route.query.name || '').trim() || '电站已成功投运',
)

const statItems = computed(() => [
  { label: '电站 ID', value: route.query.id || '—' },
  { label: '投运时间', value: formatNow() },
  { label: '状态', value: '已投运' },
])

function formatNow() {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function onFireworksComplete() {
  fireworksDone.value = true
}

function goIndex() {
  router.push({ path: '/personal/stations?tab=toApprove' })
}

onMounted(() => {
  reducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  if (reducedMotion.value) fireworksDone.value = true
})
</script>

<style scoped lang="scss">
.approve-success-page {
  box-sizing: border-box;
  overflow: hidden;
}

.approve-success {
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(8, 40, 87, 0.05);
  background:
    linear-gradient(187deg, rgba(188, 201, 250, 0.35) 0%, rgba(255, 255, 255, 0.92) 38%, #fff 100%);
  color: var(--color-text);
  padding: 48px 24px 112px;
}

.approve-success__glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(72px);
  pointer-events: none;
  z-index: 0;
  opacity: 0.45;
}

.approve-success__glow--a {
  width: 280px;
  height: 280px;
  top: 8%;
  left: 12%;
  background: color-mix(in srgb, var(--color-primary) 28%, transparent);
  animation: glow-drift 10s ease-in-out infinite alternate;
}

.approve-success__glow--b {
  width: 240px;
  height: 240px;
  bottom: 20%;
  right: 10%;
  background: color-mix(in srgb, var(--color-success) 22%, transparent);
  animation: glow-drift 12s ease-in-out infinite alternate-reverse;
}

.approve-success__content {
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 560px;
  animation: content-rise 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
}

.approve-success__title {
  margin: 0 0 10px;
  font-size: clamp(1.75rem, 4vw, 2.25rem);
  font-weight: 700;
  letter-spacing: 0.02em;
  color: var(--color-text);
  animation: title-fade 0.65s ease 0.3s both;
}

.approve-success__station {
  margin: 0 0 8px;
  font-size: clamp(1.0625rem, 2.2vw, 1.25rem);
  font-weight: 600;
  color: var(--color-primary);
  animation: title-fade 0.65s ease 0.45s both;
}

.approve-success__desc {
  margin: 0 0 28px;
  font-size: 0.9375rem;
  line-height: 1.65;
  color: color-mix(in srgb, var(--color-text) 72%, #fff);
  animation: title-fade 0.65s ease 0.55s both;
}

.approve-success__stats {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  animation: title-fade 0.65s ease 0.7s both;
}

.approve-success__stat {
  min-width: 140px;
  padding: 14px 18px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.85);
}

.approve-success__stat-value {
  display: block;
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 4px;
}

.approve-success__stat-label {
  font-size: 0.75rem;
  color: color-mix(in srgb, var(--color-text) 55%, #fff);
}

.approve-success__footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: flex;
  justify-content: center;
  padding: 20px 24px 28px;
  background: linear-gradient(180deg, transparent 0%, rgba(255, 255, 255, 0.92) 45%, #fff 100%);
  animation: title-fade 0.65s ease 0.85s both;
}

.approve-success__back-btn {
  min-width: 220px;
  box-shadow: 0 6px 16px color-mix(in srgb, var(--color-primary) 22%, transparent);
  cursor: pointer;
}

@keyframes title-fade {
  from {
    opacity: 0;
    transform: translateY(12px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes content-rise {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes glow-drift {
  from {
    transform: translate(0, 0);
  }

  to {
    transform: translate(18px, -12px);
  }
}

@media (max-width: 599px) {
  .approve-success {
    padding-bottom: 100px;
  }

  .approve-success__stat {
    min-width: calc(50% - 18px);
    flex: 1 1 calc(50% - 18px);
  }
}

@media (prefers-reduced-motion: reduce) {

  .approve-success__glow,
  .approve-success__title,
  .approve-success__content,
  .approve-success__footer {
    animation: none !important;
  }
}
</style>
